import type RestApi from 'front-api'
import type {Translation} from 'front-api/src/utils/common'
import type {WebSocketConnectionUser} from './models'
import {globalChatsStore} from './ChatsStore'

export * from './models'
export * from './connect-chat'
export * from './connect-helpers'
export * from './utils'
export * from './ChatsStore'
export * from './ChatStore'

interface UiMethods {
  readonly hideLoading: () => void
  readonly showLoading: () => void
}

interface NotifierMethods {
  readonly showNotifierError: (message: string) => void
  readonly showNotifierSuccess: (message: string) => void
}

export type UploadChatMessageParams = {[index: string]: string}

interface SystemMethods {
  readonly mediaPathMapper: (uri: string) => string
  readonly sendMedia: (
    endpoint: string,
    partName: string,
    data: string | Blob,
    parameters: UploadChatMessageParams,
  ) => void
  readonly saveDisabledAutoTranslationChatIds: (chatIds: Set<string>) => void
  readonly loadDisabledAutoTranslationChatIds: () => Promise<Set<string>>
}

interface AdvertoChatDeps {
  readonly restApi: RestApi
  readonly t: Translation
  readonly langProvider: () => string
  readonly ui: UiMethods
  readonly notifier: NotifierMethods
  readonly system: SystemMethods
}

interface AdvertoChatOptions {
  readonly chatsEndpointOverrides?: string
  readonly isStaging: boolean
  readonly debug?: boolean
  readonly connectionParams: () => Promise<WebSocketConnectionUser | undefined>
}

export class Chats {
  private static instance: Chats

  static async init(params: {
    deps: AdvertoChatDeps
    options: AdvertoChatOptions
  }) {
    this.instance = new Chats(params.deps, params.options)
    const ids = await Chats.system.loadDisabledAutoTranslationChatIds()
    globalChatsStore.setDisabledAutoTranslationChatIds(ids)
  }

  private static get deps(): AdvertoChatDeps {
    return this.assertInstance().deps
  }

  static get options(): AdvertoChatOptions {
    return this.assertInstance().options
  }

  static get t(): Translation {
    return this.deps.t
  }

  static get lang(): string {
    return this.deps.langProvider()
  }

  static get api(): RestApi {
    return this.deps.restApi
  }

  static get ui(): UiMethods {
    return this.deps.ui
  }

  static get notifier(): NotifierMethods {
    return this.deps.notifier
  }

  static get system(): SystemMethods {
    return this.deps.system
  }

  private static assertInstance(): Chats {
    if (!Chats.instance) {
      throw new Error(
        'VooxeeChats not initialized. Invoke VooxeeChats.init(...) to initialize chats.',
      )
    }
    return Chats.instance
  }

  constructor(
    private deps: AdvertoChatDeps,
    private options: AdvertoChatOptions,
  ) {}
}
